import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { resourceTypes } from '@quality-vet/contracts';

import type { Membership, OrganisationLocation, Resource, ResourceCategory } from '../domain-types';
import './resources-panel.css';

type ResourceListItem = Resource & {
  alerts: string[];
  health: 'good' | 'attention' | 'action_required';
  currentApprovedVersion?: Resource['versions'][number];
};

type ResourceDashboard = {
  totalActive: number;
  generatedAt: string;
  categories: Record<ResourceCategory, { count: number; alertCount: number }>;
  alerts: {
    reviewDue: number;
    maintenanceDue: number;
    expiring: number;
    unverified: number;
    awaitingApproval: number;
    unavailableOrSuspended: number;
    recentlyUpdated: number;
  };
};

type TgaScopeItem = { nationalCode: string; title: string; trainingComponentType: string };
type ResourceReminderPolicy = {
  reminderDays: number[];
  escalationPersonId?: string;
  weeklyDigestEnabled: boolean;
};
type ResourceImportPreview = {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  errors: Array<{ row: number; message: string }>;
  ready: boolean;
};
type ResourceSavedView = {
  id: string;
  name: string;
  reportType: string;
  filters: { search?: string; category?: ResourceCategory; alertsOnly?: boolean };
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};

const categoryLabels: Record<ResourceCategory, string> = {
  facilities: 'Facilities',
  vehicles: 'Vehicles',
  equipment: 'Equipment',
  personnel: 'Personnel',
  training_learning: 'Training & Learning',
  assessment: 'Assessment',
  student_support: 'Student Support',
  digital_licensing: 'Digital Licensing',
  documents: 'Documents',
  other: 'Other',
};

const categories = Object.keys(categoryLabels) as ResourceCategory[];
const typeLabel = (value: string) =>
  value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .replaceAll('Rto', 'RTO')
    .replaceAll('Llnd', 'LLND')
    .replaceAll('Ppe', 'PPE')
    .replaceAll('Av', 'AV')
    .replaceAll('It', 'IT')
    .replaceAll('Rpl', 'RPL');

const statusLabel = (value: string) => value.replaceAll('_', ' ');
const dateLabel = (value?: string) =>
  value
    ? new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium' }).format(new Date(value))
    : 'Not set';

function badgeClass(value: string): string {
  if (['approved', 'available', 'active', 'good', 'complete', 'passed'].includes(value))
    return ' is-good';
  if (
    [
      'unverified',
      'rejected',
      'approval_expired',
      'out_of_service',
      'withdrawn',
      'action_required',
      'failed',
    ].includes(value)
  )
    return ' is-risk';
  return ' is-attention';
}

function categoryDetails(
  category: ResourceCategory,
  locationId: string,
  personnelUserId: string,
  otherSubtype: string,
) {
  switch (category) {
    case 'facilities':
      return { category, details: { organisationLocationId: locationId } };
    case 'equipment':
      return { category, details: { trackingMode: 'resource_pool', quantity: 1 } };
    case 'personnel':
      return { category, details: { membershipUserId: personnelUserId } };
    case 'student_support':
      return { category, details: { onlineAvailable: false } };
    case 'digital_licensing':
      return { category, details: { autoRenewal: false } };
    case 'documents':
      return { category, details: { acknowledgementRequired: false } };
    case 'other':
      return { category, details: { ...(otherSubtype ? { otherSubtype } : {}) } };
    default:
      return { category, details: {} };
  }
}

function CategoryDetailsEditor({
  category,
  details,
  onChange,
}: {
  category: ResourceCategory;
  details: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  const scalar = (value: unknown) =>
    typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  const text = (label: string, key: string, type: 'text' | 'number' | 'url' = 'text') => (
    <label>
      {label}
      <input
        type={type}
        value={scalar(details[key])}
        onChange={(event) =>
          onChange(
            key,
            type === 'number'
              ? event.target.value
                ? Number(event.target.value)
                : undefined
              : event.target.value || undefined,
          )
        }
      />
    </label>
  );
  const textarea = (label: string, key: string) => (
    <label className="resources-form-wide">
      {label}
      <textarea
        value={scalar(details[key])}
        onChange={(event) => onChange(key, event.target.value || undefined)}
      />
    </label>
  );
  const list = (label: string, key: string) => (
    <label>
      {label}
      <input
        value={Array.isArray(details[key]) ? details[key].join(', ') : ''}
        onChange={(event) =>
          onChange(
            key,
            event.target.value
              .split(',')
              .map((value) => value.trim())
              .filter(Boolean),
          )
        }
      />
    </label>
  );
  const toggle = (label: string, key: string) => (
    <label className="resources-check">
      <input
        checked={details[key] === true}
        type="checkbox"
        onChange={(event) => onChange(key, event.target.checked)}
      />{' '}
      {label}
    </label>
  );
  switch (category) {
    case 'facilities':
      return (
        <>
          {text('Room / facility identifier', 'identifier')}
          {text('Floor / area', 'floorOrArea')}
          {text('Capacity', 'capacity', 'number')}
          {text('Maximum student capacity', 'maximumStudentCapacity', 'number')}
          {textarea('Accessibility', 'accessibility')}
          {list('Amenities (comma separated)', 'amenities')}
          {list('Delivery modes (comma separated)', 'deliveryModes')}
          {textarea('Safety information', 'safetyInformation')}
          {textarea('Emergency information', 'emergencyInformation')}
          {text('Operating hours', 'operatingHours')}
          {textarea('Access restrictions', 'accessRestrictions')}
        </>
      );
    case 'vehicles':
      return (
        <>
          {text('Registration number', 'registrationNumber')}
          {text('VIN', 'vin')}
          {text('Make', 'make')}
          {text('Model', 'model')}
          {text('Year', 'year', 'number')}
          {text('Odometer', 'odometer', 'number')}
          {text('Capacity', 'capacity', 'number')}
          {text('Licence class required', 'licenceClassRequired')}
        </>
      );
    case 'equipment':
      return (
        <>
          <label>
            Tracking mode
            <select
              value={scalar(details.trackingMode) || 'resource_pool'}
              onChange={(event) => onChange('trackingMode', event.target.value)}
            >
              <option value="resource_pool">Resource pool</option>
              <option value="individual_asset">Individual asset</option>
            </select>
          </label>
          {text('Serial number', 'serialNumber')}
          {text('Make', 'make')}
          {text('Model', 'model')}
          {text('Quantity', 'quantity', 'number')}
          {text('Available quantity', 'availableQuantity', 'number')}
          {text('Minimum required quantity', 'minimumRequiredQuantity', 'number')}
          {text('Condition', 'condition')}
          {toggle('Calibration required', 'calibrationRequired')}
        </>
      );
    case 'personnel':
      return (
        <>
          {text('Capacity', 'capacity', 'number')}
          {text('Credential status', 'credentialStatus')}
          {list('Licences (comma separated)', 'licences')}
          {text('Industry currency review', 'industryCurrencyReviewAt')}
          {text('Capability review', 'capabilityReviewAt')}
        </>
      );
    case 'training_learning':
      return (
        <>
          {text('Format', 'format')}
          {text('Author / developer', 'authorDeveloper')}
          {text('Copyright holder', 'copyrightHolder')}
          {text('Licence', 'licence')}
          {textarea('Accessibility review', 'accessibilityReview')}
          {text('Mapping status', 'mappingStatus')}
        </>
      );
    case 'assessment':
      return (
        <>
          {list('Components (comma separated)', 'componentList')}
          {text('Security classification', 'securityClassification')}
          {textarea('Trialling information', 'triallingInformation')}
          {textarea('Validation history', 'validationHistory')}
          {textarea('Reasonable adjustment considerations', 'reasonableAdjustmentConsiderations')}
        </>
      );
    case 'student_support':
      return (
        <>
          {text('Service provider', 'serviceProvider')}
          <label>
            Delivery
            <select
              value={scalar(details.internalExternal)}
              onChange={(event) => onChange('internalExternal', event.target.value || undefined)}
            >
              <option value="">Not set</option>
              <option value="internal">Internal</option>
              <option value="external">External</option>
            </select>
          </label>
          {toggle('Available online', 'onlineAvailable')}
          {textarea('Eligibility', 'eligibility')}
          {textarea('Access / referral pathway', 'accessPathway')}
          {text('Service hours', 'serviceHours')}
          {text('Contact', 'contact')}
          {text('Student cost', 'studentCost', 'number')}
          {text('Organisation cost', 'organisationCost', 'number')}
        </>
      );
    case 'digital_licensing':
      return (
        <>
          {text('System / service name', 'serviceName')}
          {text('Licence type', 'licenceType')}
          {text('Licence number', 'licenceNumber')}
          {text('Service URL', 'url', 'url')}
          {text('Seats', 'seats', 'number')}
          {text('Allocated seats', 'allocatedSeats', 'number')}
          {toggle('Auto renewal', 'autoRenewal')}
          {text('Subscription cost', 'subscriptionCost', 'number')}
          {textarea('Accessibility review', 'accessibilityReview')}
          {textarea('Privacy / security review', 'privacySecurityReview')}
        </>
      );
    case 'documents':
      return (
        <>
          {text('Document ID', 'documentId')}
          {text('Organisation function', 'organisationFunction')}
          {textarea('Distribution', 'distribution')}
          {toggle('Acknowledgement required', 'acknowledgementRequired')}
        </>
      );
    case 'other':
      return <>{text('Other subtype', 'otherSubtype')}</>;
  }
}

function parseCsv(text: string): Array<Record<string, string>> {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index]!;
    if (character === '"') {
      if (quoted && text[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else quoted = !quoted;
    } else if (character === ',' && !quoted) {
      row.push(cell.trim());
      cell = '';
    } else if ((character === '\n' || character === '\r') && !quoted) {
      if (character === '\r' && text[index + 1] === '\n') index += 1;
      row.push(cell.trim());
      if (row.some(Boolean)) rows.push(row);
      row = [];
      cell = '';
    } else cell += character;
  }
  row.push(cell.trim());
  if (row.some(Boolean)) rows.push(row);
  const headers = rows.shift()?.map((value) => value.trim().toLowerCase());
  if (!headers?.length) return [];
  return rows.map((values) =>
    Object.fromEntries(headers.map((header, index) => [header, values[index] ?? ''])),
  );
}

export function ResourcesPanel({
  apiUrl,
  accessToken,
  tenantId,
  locations,
  members,
  canApprove,
  platformSupportReason,
}: {
  apiUrl: string;
  accessToken: string;
  tenantId: string;
  locations: OrganisationLocation[];
  members: Membership[];
  canApprove: boolean;
  platformSupportReason?: string | undefined;
}) {
  const [items, setItems] = useState<ResourceListItem[]>([]);
  const [dashboard, setDashboard] = useState<ResourceDashboard | null>(null);
  const [selected, setSelected] = useState<ResourceListItem | null>(null);
  const [selectedTab, setSelectedTab] = useState<'summary' | 'details'>('summary');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<ResourceCategory | ''>('');
  const [alertsOnly, setAlertsOnly] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [scope, setScope] = useState<TgaScopeItem[]>([]);
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState<ResourceCategory>('facilities');
  const [newType, setNewType] = useState<string>(resourceTypes.facilities[0]);
  const [newProvision, setNewProvision] = useState('rto_owned');
  const [newLocationId, setNewLocationId] = useState('');
  const [newPersonnelUserId, setNewPersonnelUserId] = useState('');
  const [newOtherSubtype, setNewOtherSubtype] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [keyDateType, setKeyDateType] = useState('review_due');
  const [keyDateDueAt, setKeyDateDueAt] = useState('');
  const [reviewReason, setReviewReason] = useState('scheduled_periodic');
  const [reviewDueAt, setReviewDueAt] = useState('');
  const [maintenanceType, setMaintenanceType] = useState('inspection');
  const [maintenanceDueAt, setMaintenanceDueAt] = useState('');
  const [issueTitle, setIssueTitle] = useState('');
  const [issueDescription, setIssueDescription] = useState('');
  const [mappingCode, setMappingCode] = useState('');
  const [mappingRelease, setMappingRelease] = useState('');
  const [relatedResourceId, setRelatedResourceId] = useState('');
  const [relationshipType, setRelationshipType] = useState('requires');
  const [resourceFile, setResourceFile] = useState<File | null>(null);
  const [resourceFileDescription, setResourceFileDescription] = useState('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importItems, setImportItems] = useState<unknown[]>([]);
  const [importPreview, setImportPreview] = useState<ResourceImportPreview | null>(null);
  const [importErrors, setImportErrors] = useState<Array<{ row: number; message: string }>>([]);
  const [savedViews, setSavedViews] = useState<ResourceSavedView[]>([]);
  const [activeViewId, setActiveViewId] = useState('');
  const [reportType, setReportType] = useState('complete_register');
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editInternalReference, setEditInternalReference] = useState('');
  const [editAssetNumber, setEditAssetNumber] = useState('');
  const [editSupplier, setEditSupplier] = useState('');
  const [editAvailability, setEditAvailability] = useState('available');
  const [editLifecycle, setEditLifecycle] = useState('draft');
  const [editDetails, setEditDetails] = useState<Record<string, unknown>>({});
  const [completeReviewId, setCompleteReviewId] = useState('');
  const [reviewFindings, setReviewFindings] = useState('');
  const [reviewDecision, setReviewDecision] = useState('suitable');
  const [reviewActions, setReviewActions] = useState('');
  const [reviewNextDueAt, setReviewNextDueAt] = useState('');
  const [reviewEvidenceFileIds, setReviewEvidenceFileIds] = useState<string[]>([]);
  const [maintenanceScheduleId, setMaintenanceScheduleId] = useState('');
  const [maintenanceOutcome, setMaintenanceOutcome] = useState('passed');
  const [maintenanceIssues, setMaintenanceIssues] = useState('');
  const [maintenanceCost, setMaintenanceCost] = useState('');
  const [maintenanceNextDueAt, setMaintenanceNextDueAt] = useState('');
  const [maintenanceEvidenceFileIds, setMaintenanceEvidenceFileIds] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState<
    'archive' | 'assign_locations' | 'assign_owner' | 'update_review_date' | 'map_training_product'
  >('archive');
  const [bulkLocationId, setBulkLocationId] = useState('');
  const [bulkOwnerId, setBulkOwnerId] = useState('');
  const [bulkReviewDate, setBulkReviewDate] = useState('');
  const [reminderPolicy, setReminderPolicy] = useState<ResourceReminderPolicy | null>(null);
  const [reminderDays, setReminderDays] = useState('30, 14, 7, 0');
  const [escalationPersonId, setEscalationPersonId] = useState('');
  const [weeklyDigestEnabled, setWeeklyDigestEnabled] = useState(true);

  const requestHeaders = useMemo(
    () => ({
      authorization: `Bearer ${accessToken}`,
      ...(platformSupportReason ? { 'x-quality-vet-support-reason': platformSupportReason } : {}),
    }),
    [accessToken, platformSupportReason],
  );
  const tenantPath = (path: string) => `${apiUrl}/v1/tenants/${tenantId}/${path}`;

  const request = async <T,>(path: string, options: { method?: string; body?: unknown } = {}) => {
    const response = await fetch(tenantPath(path), {
      method: options.method ?? 'GET',
      headers: {
        ...requestHeaders,
        ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
      },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
    if (!response.ok) {
      const body: unknown = await response.json().catch(() => undefined);
      const message =
        typeof body === 'object' &&
        body !== null &&
        'message' in body &&
        typeof body.message === 'string'
          ? body.message
          : 'The Resources request could not be completed.';
      throw new Error(message);
    }
    const payload: unknown = await response.json();
    return payload as T;
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const parameters = new URLSearchParams({ pageSize: '100' });
      if (search.trim()) parameters.set('search', search.trim());
      if (category) parameters.set('category', category);
      if (alertsOnly) parameters.set('alertsOnly', 'true');
      const [register, summary] = await Promise.all([
        request<{ items: ResourceListItem[] }>(`resources?${parameters.toString()}`),
        request<ResourceDashboard>('resources/dashboard'),
      ]);
      setItems(register.items);
      setDashboard(summary);
      setSelected((current) => register.items.find((item) => item.id === current?.id) ?? null);
      setSelectedIds((current) =>
        current.filter((id) => register.items.some((item) => item.id === id)),
      );
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Resources could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [tenantId, search, category, alertsOnly]);

  useEffect(() => {
    if (!tenantId) return;
    void request<{ items: TgaScopeItem[] }>('provider/scope')
      .then((result) => setScope(result.items))
      .catch(() => setScope([]));
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !canApprove) return;
    void request<ResourceReminderPolicy>('resources/reminder-policy')
      .then((policy) => {
        setReminderPolicy(policy);
        setReminderDays(policy.reminderDays.join(', '));
        setEscalationPersonId(policy.escalationPersonId ?? '');
        setWeeklyDigestEnabled(policy.weeklyDigestEnabled);
      })
      .catch(() => setReminderPolicy(null));
  }, [tenantId, canApprove]);

  useEffect(() => {
    if (!tenantId) return;
    void request<{ items: ResourceSavedView[] }>('resources/views')
      .then((result) => setSavedViews(result.items))
      .catch(() => setSavedViews([]));
  }, [tenantId]);

  const select = async (id: string) => {
    setBusy(true);
    setError('');
    try {
      const detail = await request<ResourceListItem>(`resources/${id}`);
      setSelected(detail);
      setSelectedTab('summary');
    } catch (selectError) {
      setError(
        selectError instanceof Error ? selectError.message : 'The resource could not be opened.',
      );
    } finally {
      setBusy(false);
    }
  };

  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      if (newCategory === 'facilities' && !newLocationId)
        throw new Error('Select the Organisation Location for this facility.');
      if (newCategory === 'personnel' && !newPersonnelUserId)
        throw new Error('Select the existing Organisation Personnel record.');
      if (newCategory === 'other' && newType === 'other_resource' && !newOtherSubtype.trim())
        throw new Error('Enter the subtype for Other Resource.');
      const locationRole =
        newCategory === 'facilities'
          ? 'primary'
          : newCategory === 'vehicles'
            ? 'home_base'
            : 'permitted';
      const created = await request<ResourceListItem>('resources', {
        method: 'POST',
        body: {
          name: newName,
          category: newCategory,
          type: newType,
          provision: newProvision,
          ...(newDescription.trim() ? { description: newDescription.trim() } : {}),
          locations: newLocationId ? [{ locationId: newLocationId, role: locationRole }] : [],
          categoryDetails: categoryDetails(
            newCategory,
            newLocationId,
            newPersonnelUserId,
            newOtherSubtype.trim(),
          ),
        },
      });
      setShowCreate(false);
      setNewName('');
      setNewDescription('');
      setNewOtherSubtype('');
      await load();
      setSelected(created);
    } catch (createError) {
      setError(
        createError instanceof Error ? createError.message : 'The resource could not be created.',
      );
    } finally {
      setBusy(false);
    }
  };

  const updateSelected = async (path: string, body: unknown, method = 'POST') => {
    if (!selected) return;
    setBusy(true);
    setError('');
    try {
      const updated = await request<ResourceListItem>(
        `resources/${selected.id}${path ? `/${path}` : ''}`,
        { method, body },
      );
      setSelected(updated);
      await load();
    } catch (updateError) {
      setError(
        updateError instanceof Error ? updateError.message : 'The resource could not be updated.',
      );
    } finally {
      setBusy(false);
    }
  };

  const exportRegister = async () => {
    setBusy(true);
    setError('');
    try {
      const parameters = new URLSearchParams({ reportType });
      if (search.trim()) parameters.set('search', search.trim());
      if (category) parameters.set('category', category);
      if (alertsOnly) parameters.set('alertsOnly', 'true');
      const response = await fetch(tenantPath(`resources/report?${parameters.toString()}`), {
        headers: requestHeaders,
      });
      if (!response.ok) throw new Error('The Resource Register export could not be prepared.');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `quality-vet-${reportType.replaceAll('_', '-')}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (exportError) {
      setError(
        exportError instanceof Error
          ? exportError.message
          : 'The Resource Register export could not be prepared.',
      );
    } finally {
      setBusy(false);
    }
  };

  const previewImport = async (file: File | null) => {
    setImportFile(file);
    setImportItems([]);
    setImportPreview(null);
    setImportErrors([]);
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const rows = parseCsv(await file.text());
      if (!rows.length) throw new Error('The CSV file has no data rows.');
      const clientErrors: Array<{ row: number; message: string }> = [];
      const candidates: unknown[] = [];
      const previewCandidates: unknown[] = [];
      rows.forEach((row, index) => {
        const rowNumber = index + 2;
        const importedCategory = row.category as ResourceCategory;
        const importedType = row.type;
        const locationId = row.locationid || row.location_id;
        const personnelUserId = row.personneluserid || row.personnel_user_id;
        const otherSubtype = row.othersubtype || row.other_subtype;
        if (!categories.includes(importedCategory)) {
          clientErrors.push({ row: rowNumber, message: 'Category is not recognised.' });
          return;
        }
        if (
          !importedType ||
          !(resourceTypes[importedCategory] as readonly string[]).includes(importedType)
        ) {
          clientErrors.push({
            row: rowNumber,
            message: 'Item type does not belong to the selected category.',
          });
          return;
        }
        if (!row.name) {
          clientErrors.push({ row: rowNumber, message: 'Resource name is required.' });
          return;
        }
        if (importedCategory === 'facilities' && !locationId) {
          clientErrors.push({
            row: rowNumber,
            message: 'Facilities need an Organisation Location ID.',
          });
          return;
        }
        if (importedCategory === 'personnel' && !personnelUserId) {
          clientErrors.push({
            row: rowNumber,
            message: 'Personnel resources need an existing Personnel user ID.',
          });
          return;
        }
        if (importedCategory === 'other' && importedType === 'other_resource' && !otherSubtype) {
          clientErrors.push({ row: rowNumber, message: 'Other Resource needs a subtype.' });
          return;
        }
        const locationRole =
          importedCategory === 'facilities'
            ? 'primary'
            : importedCategory === 'vehicles'
              ? 'home_base'
              : 'permitted';
        const candidate = {
          name: row.name,
          category: importedCategory,
          type: importedType,
          provision: row.provision || 'rto_owned',
          ...(row.description ? { description: row.description } : {}),
          locations: locationId ? [{ locationId, role: locationRole }] : [],
          categoryDetails: categoryDetails(
            importedCategory,
            locationId ?? '',
            personnelUserId ?? '',
            otherSubtype ?? '',
          ),
        };
        candidates.push(candidate);
        previewCandidates.push({ __row: rowNumber, record: candidate });
      });
      if (candidates.length) {
        const serverPreview = await request<ResourceImportPreview>('resources/import-preview', {
          method: 'POST',
          body: { items: previewCandidates },
        });
        setImportPreview({
          ...serverPreview,
          totalRows: rows.length,
          validRows: candidates.length - serverPreview.invalidRows,
          invalidRows: clientErrors.length + serverPreview.invalidRows,
          ready: clientErrors.length === 0 && serverPreview.ready,
        });
        setImportErrors([...clientErrors, ...serverPreview.errors]);
      } else {
        setImportPreview({
          totalRows: rows.length,
          validRows: 0,
          invalidRows: clientErrors.length,
          errors: clientErrors,
          ready: false,
        });
        setImportErrors(clientErrors);
      }
      setImportItems(candidates);
    } catch (importError) {
      setError(
        importError instanceof Error
          ? importError.message
          : 'The Resource Register import could not be previewed.',
      );
    } finally {
      setBusy(false);
    }
  };

  const downloadImportErrors = () => {
    const rows = [
      ['Row', 'Error'],
      ...importErrors.map((entry) => [String(entry.row), entry.message]),
    ];
    const csv = rows
      .map((row) => row.map((value) => `"${value.replaceAll('"', '""')}"`).join(','))
      .join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'quality-vet-resource-import-errors.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const importRegister = async () => {
    if (!importPreview?.ready || !importItems.length) return;
    setBusy(true);
    setError('');
    try {
      await request<{ imported: number }>('resources/import', {
        method: 'POST',
        body: { items: importItems },
      });
      setImportFile(null);
      setImportItems([]);
      setImportPreview(null);
      setImportErrors([]);
      await load();
    } catch (importError) {
      setError(
        importError instanceof Error
          ? importError.message
          : 'The Resource Register import could not be completed.',
      );
    } finally {
      setBusy(false);
    }
  };

  const saveCurrentView = async () => {
    const name = window
      .prompt('Name this Resource view or report template (for example, Vehicle Register).')
      ?.trim();
    if (!name) return;
    setBusy(true);
    setError('');
    try {
      const created = await request<ResourceSavedView>('resources/views', {
        method: 'POST',
        body: {
          name,
          reportType,
          filters: {
            ...(search.trim() ? { search: search.trim() } : {}),
            ...(category ? { category } : {}),
            ...(alertsOnly ? { alertsOnly } : {}),
          },
        },
      });
      setSavedViews((current) =>
        [...current, created].sort((left, right) => left.name.localeCompare(right.name)),
      );
      setActiveViewId(created.id);
    } catch (viewError) {
      setError(
        viewError instanceof Error ? viewError.message : 'The Resource view could not be saved.',
      );
    } finally {
      setBusy(false);
    }
  };

  const applySavedView = (viewId: string) => {
    setActiveViewId(viewId);
    const view = savedViews.find((item) => item.id === viewId);
    if (!view) return;
    setSearch(view.filters.search ?? '');
    setCategory(view.filters.category ?? '');
    setAlertsOnly(view.filters.alertsOnly === true);
    setReportType(view.reportType);
  };

  const deleteSavedView = async () => {
    if (!activeViewId) return;
    const view = savedViews.find((item) => item.id === activeViewId);
    if (!view || !window.confirm(`Delete the saved Resource view “${view.name}”?`)) return;
    setBusy(true);
    setError('');
    try {
      const response = await fetch(tenantPath(`resources/views/${view.id}`), {
        method: 'DELETE',
        headers: requestHeaders,
      });
      if (!response.ok) throw new Error('The saved Resource view could not be deleted.');
      setSavedViews((current) => current.filter((item) => item.id !== view.id));
      setActiveViewId('');
    } catch (viewError) {
      setError(
        viewError instanceof Error
          ? viewError.message
          : 'The saved Resource view could not be deleted.',
      );
    } finally {
      setBusy(false);
    }
  };

  const beginEdit = () => {
    if (!selected) return;
    setEditName(selected.name);
    setEditDescription(selected.description ?? '');
    setEditInternalReference(selected.internalReference ?? '');
    setEditAssetNumber(selected.assetNumber ?? '');
    setEditSupplier(selected.supplier ?? '');
    setEditAvailability(selected.availability);
    setEditLifecycle(selected.lifecycle);
    setEditDetails({ ...selected.categoryDetails.details });
    setEditing(true);
  };

  const saveEdit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected) return;
    setBusy(true);
    setError('');
    try {
      const updated = await request<ResourceListItem>(`resources/${selected.id}`, {
        method: 'PATCH',
        body: {
          name: editName,
          ...(editDescription.trim()
            ? { description: editDescription.trim() }
            : { description: undefined }),
          ...(editInternalReference.trim()
            ? { internalReference: editInternalReference.trim() }
            : { internalReference: undefined }),
          ...(editAssetNumber.trim()
            ? { assetNumber: editAssetNumber.trim() }
            : { assetNumber: undefined }),
          ...(editSupplier.trim() ? { supplier: editSupplier.trim() } : { supplier: undefined }),
          availability: editAvailability,
          lifecycle: editLifecycle,
          categoryDetails: { category: selected.category, details: editDetails },
        },
      });
      setSelected(updated);
      setEditing(false);
      await load();
    } catch (editError) {
      setError(
        editError instanceof Error ? editError.message : 'The Resource details could not be saved.',
      );
    } finally {
      setBusy(false);
    }
  };

  const completeReview = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!completeReviewId) return;
    await updateSelected(`reviews/${completeReviewId}/complete`, {
      status: 'complete',
      decision: reviewDecision,
      ...(reviewFindings.trim() ? { findings: reviewFindings.trim() } : {}),
      ...(reviewActions.trim() ? { actionsRequired: reviewActions.trim() } : {}),
      ...(reviewNextDueAt ? { nextReviewAt: new Date(reviewNextDueAt).toISOString() } : {}),
      evidenceFileIds: reviewEvidenceFileIds,
      responses: [],
    });
    setCompleteReviewId('');
    setReviewFindings('');
    setReviewActions('');
    setReviewNextDueAt('');
    setReviewEvidenceFileIds([]);
  };

  const completeMaintenance = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected || !maintenanceScheduleId) return;
    const schedule = selected.maintenanceSchedules.find(
      (item) => item.id === maintenanceScheduleId,
    );
    if (!schedule) return;
    await updateSelected('maintenance-events', {
      scheduleId: schedule.id,
      maintenanceType: schedule.maintenanceType,
      completedAt: new Date().toISOString(),
      outcome: maintenanceOutcome,
      ...(maintenanceIssues.trim() ? { issuesIdentified: maintenanceIssues.trim() } : {}),
      ...(maintenanceCost ? { cost: Number(maintenanceCost) } : {}),
      ...(maintenanceNextDueAt ? { nextDueAt: new Date(maintenanceNextDueAt).toISOString() } : {}),
      evidenceFileIds: maintenanceEvidenceFileIds,
    });
    setMaintenanceScheduleId('');
    setMaintenanceIssues('');
    setMaintenanceCost('');
    setMaintenanceNextDueAt('');
    setMaintenanceEvidenceFileIds([]);
  };

  const applyBulkAction = async () => {
    if (!selectedIds.length) return;
    setBusy(true);
    setError('');
    try {
      let body: unknown;
      if (bulkAction === 'assign_locations') {
        if (!bulkLocationId) throw new Error('Choose the Location to assign.');
        body = {
          operation: bulkAction,
          resourceIds: selectedIds,
          locations: [{ locationId: bulkLocationId, role: 'primary' }],
        };
      } else if (bulkAction === 'assign_owner') {
        if (!bulkOwnerId) throw new Error('Choose the Resource owner.');
        body = { operation: bulkAction, resourceIds: selectedIds, ownerId: bulkOwnerId };
      } else if (bulkAction === 'update_review_date') {
        if (!bulkReviewDate) throw new Error('Choose the next review date.');
        body = {
          operation: bulkAction,
          resourceIds: selectedIds,
          nextReviewAt: new Date(bulkReviewDate).toISOString(),
        };
      } else if (bulkAction === 'map_training_product') {
        const selectedScope = scope.find((item) => item.nationalCode === mappingCode);
        if (!selectedScope || !mappingRelease.trim())
          throw new Error('Choose a scoped training product and release number.');
        body = {
          operation: bulkAction,
          resourceIds: selectedIds,
          mapping: {
            productCode: selectedScope.nationalCode,
            title: selectedScope.title,
            productType: selectedScope.trainingComponentType,
            releaseNumber: mappingRelease.trim(),
            releaseIdentifier: `${selectedScope.nationalCode}#${mappingRelease.trim()}`,
            relationship: 'supports delivery',
            locationIds: [],
            deliveryModes: [],
            assuranceStatus: 'under_review',
            unitMappings: [],
          },
        };
      } else body = { operation: 'archive', resourceIds: selectedIds };
      await request<{ updated: number }>('resources/bulk', { method: 'POST', body });
      setSelectedIds([]);
      await load();
    } catch (bulkError) {
      setError(
        bulkError instanceof Error
          ? bulkError.message
          : 'The bulk Resource operation could not be completed.',
      );
    } finally {
      setBusy(false);
    }
  };

  const saveReminderPolicy = async () => {
    setBusy(true);
    setError('');
    try {
      const days = reminderDays
        .split(',')
        .map((value) => Number(value.trim()))
        .filter((value) => Number.isInteger(value) && value >= 0);
      if (!days.length || new Set(days).size !== days.length)
        throw new Error('Use unique whole-day reminder offsets, separated by commas.');
      const policy = await request<ResourceReminderPolicy>('resources/reminder-policy', {
        method: 'PATCH',
        body: {
          reminderDays: days,
          ...(escalationPersonId ? { escalationPersonId } : {}),
          weeklyDigestEnabled,
        },
      });
      setReminderPolicy(policy);
    } catch (policyError) {
      setError(
        policyError instanceof Error
          ? policyError.message
          : 'Reminder defaults could not be saved.',
      );
    } finally {
      setBusy(false);
    }
  };

  const printEvidencePack = async () => {
    const mapping = selected?.trainingProducts[0];
    if (!mapping) {
      setError(
        'Map this Resource to a training product release before generating an evidence pack.',
      );
      return;
    }
    setBusy(true);
    setError('');
    try {
      const query = new URLSearchParams({
        productCode: mapping.productCode,
        releaseIdentifier: mapping.releaseIdentifier,
        format: 'print',
      });
      const response = await fetch(tenantPath(`resources/evidence-pack?${query.toString()}`), {
        headers: requestHeaders,
      });
      if (!response.ok) throw new Error('The printable evidence pack could not be prepared.');
      const url = URL.createObjectURL(await response.blob());
      window.open(url, '_blank', 'noopener');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (printError) {
      setError(
        printError instanceof Error
          ? printError.message
          : 'The printable evidence pack could not be prepared.',
      );
    } finally {
      setBusy(false);
    }
  };

  const uploadResourceFile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected || !resourceFile) return;
    if (resourceFile.size <= 0 || resourceFile.size > 25 * 1024 * 1024) {
      setError('Select a non-empty file no larger than 25 MB.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const prepared = await request<{
        uploadUrl: string;
        file: { id: string; contentType: string };
      }>('files/upload-url', {
        method: 'POST',
        body: {
          fileName: resourceFile.name,
          contentType: resourceFile.type || 'application/octet-stream',
        },
      });
      const uploaded = await fetch(prepared.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': prepared.file.contentType, 'content-disposition': 'attachment' },
        body: resourceFile,
      });
      if (!uploaded.ok) throw new Error('The secure file upload could not be completed.');
      const completed = await request<{ id: string }>(`files/${prepared.file.id}/complete`, {
        method: 'POST',
      });
      const updated = await request<ResourceListItem>(`resources/${selected.id}/files`, {
        method: 'POST',
        body: {
          fileId: completed.id,
          ...(resourceFileDescription.trim()
            ? { description: resourceFileDescription.trim() }
            : {}),
        },
      });
      setSelected(updated);
      setResourceFile(null);
      setResourceFileDescription('');
      await load();
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : 'The resource file could not be uploaded.',
      );
    } finally {
      setBusy(false);
    }
  };

  const currentCategoryTypes = resourceTypes[newCategory] as readonly string[];
  const selectedLocationLabels = new Map(locations.map((location) => [location.id, location.name]));
  const selectedMemberLabels = new Map(
    members.map((member) => [member.userId, member.displayName ?? member.email]),
  );

  return (
    <section aria-labelledby="resources-title" className="resources-panel">
      <header className="resources-heading">
        <div>
          <p className="eyebrow">Organisation assurance</p>
          <h1 id="resources-title">Resources</h1>
          <p>
            One register for the facilities, people, assets, learning and evidence your organisation
            relies on.
          </p>
        </div>
        <div className="resources-heading-actions">
          <button
            className="button-secondary"
            disabled={busy}
            type="button"
            onClick={() => void exportRegister()}
          >
            Export register
          </button>
          <label className="button-secondary">
            Choose CSV
            <input
              accept=".csv,text/csv"
              disabled={busy}
              type="file"
              onChange={(event) => void previewImport(event.target.files?.[0] ?? null)}
            />
          </label>
          {importPreview?.ready ? (
            <button
              className="button-secondary"
              disabled={busy}
              type="button"
              onClick={() => void importRegister()}
            >
              Confirm import ({importPreview.validRows})
            </button>
          ) : null}
          <button disabled={busy} type="button" onClick={() => setShowCreate((open) => !open)}>
            {showCreate ? 'Close' : 'Add resource'}
          </button>
        </div>
      </header>

      {error ? (
        <p className="auth-error" role="alert">
          {error}
        </p>
      ) : null}
      {importFile && importPreview ? (
        <section className="resources-import-preview" aria-live="polite">
          <strong>
            CSV import preview: {importPreview.validRows} ready, {importPreview.invalidRows} needing
            attention.
          </strong>
          {importErrors.length ? (
            <>
              <ul>
                {importErrors.slice(0, 8).map((entry) => (
                  <li key={`${entry.row}-${entry.message}`}>
                    Row {entry.row}: {entry.message}
                  </li>
                ))}
              </ul>
              <button className="button-secondary" type="button" onClick={downloadImportErrors}>
                Download error report
              </button>
            </>
          ) : (
            <p>Everything is valid. Confirm the import when ready.</p>
          )}
        </section>
      ) : null}

      {dashboard ? (
        <>
          <div aria-label="Resource alert dashboard" className="resources-alert-grid">
            {[
              ['Review due', dashboard.alerts.reviewDue],
              ['Maintenance due', dashboard.alerts.maintenanceDue],
              ['Expiring', dashboard.alerts.expiring],
              ['Unverified', dashboard.alerts.unverified],
              ['Awaiting approval', dashboard.alerts.awaitingApproval],
              ['Unavailable / suspended', dashboard.alerts.unavailableOrSuspended],
              ['Recently updated', dashboard.alerts.recentlyUpdated],
            ].map(([label, count]) => (
              <button key={String(label)} type="button" onClick={() => setAlertsOnly(true)}>
                <strong>{count}</strong>
                <span>{label}</span>
              </button>
            ))}
          </div>
          <div aria-label="Resource categories" className="resources-category-grid">
            {categories.map((resourceCategory) => (
              <button
                key={resourceCategory}
                type="button"
                onClick={() => setCategory(resourceCategory)}
              >
                <strong>{dashboard.categories[resourceCategory].count}</strong>
                <span>{categoryLabels[resourceCategory]}</span>
                {dashboard.categories[resourceCategory].alertCount ? (
                  <small>{dashboard.categories[resourceCategory].alertCount} alert(s)</small>
                ) : null}
              </button>
            ))}
          </div>
          {canApprove && reminderPolicy ? (
            <details className="resources-reminder-policy">
              <summary>Reminder defaults and weekly digest</summary>
              <div className="resources-inline-form">
                <label>
                  Days before due
                  <input
                    aria-label="Reminder days"
                    value={reminderDays}
                    onChange={(event) => setReminderDays(event.target.value)}
                  />
                </label>
                <label>
                  Escalate to
                  <select
                    aria-label="Escalation recipient"
                    value={escalationPersonId}
                    onChange={(event) => setEscalationPersonId(event.target.value)}
                  >
                    <option value="">Organisation Administrators</option>
                    {members
                      .filter((member) => member.status === 'active')
                      .map((member) => (
                        <option key={member.userId} value={member.userId}>
                          {member.displayName ?? member.email}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="resources-check">
                  <input
                    checked={weeklyDigestEnabled}
                    type="checkbox"
                    onChange={(event) => setWeeklyDigestEnabled(event.target.checked)}
                  />{' '}
                  Weekly due and overdue digest
                </label>
                <button disabled={busy} type="button" onClick={() => void saveReminderPolicy()}>
                  Save defaults
                </button>
              </div>
            </details>
          ) : null}
        </>
      ) : null}

      {showCreate ? (
        <form className="resources-create-form" onSubmit={(event) => void create(event)}>
          <h2>Add to the Resource Register</h2>
          <label>
            Resource name
            <input required value={newName} onChange={(event) => setNewName(event.target.value)} />
          </label>
          <label>
            Category
            <select
              value={newCategory}
              onChange={(event) => {
                const value = event.target.value as ResourceCategory;
                setNewCategory(value);
                setNewType(resourceTypes[value][0]);
              }}
            >
              {categories.map((value) => (
                <option key={value} value={value}>
                  {categoryLabels[value]}
                </option>
              ))}
            </select>
          </label>
          <label>
            Item type
            <select value={newType} onChange={(event) => setNewType(event.target.value)}>
              {currentCategoryTypes.map((value) => (
                <option key={value} value={value}>
                  {typeLabel(value)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Provision
            <select value={newProvision} onChange={(event) => setNewProvision(event.target.value)}>
              {[
                'rto_owned',
                'purchased',
                'leased',
                'hired',
                'licensed',
                'other_party_provided',
                'employer_provided',
                'work_placement_provider_supplied',
                'student_supplied',
                'other',
              ].map((value) => (
                <option key={value} value={value}>
                  {statusLabel(value)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Organisation Location
            <select
              value={newLocationId}
              onChange={(event) => setNewLocationId(event.target.value)}
            >
              <option value="">Organisation-wide / no fixed location</option>
              {locations
                .filter((location) => location.status !== 'archived')
                .map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
            </select>
          </label>
          {newCategory === 'personnel' ? (
            <label>
              Existing personnel
              <select
                value={newPersonnelUserId}
                onChange={(event) => setNewPersonnelUserId(event.target.value)}
              >
                <option value="">Select person</option>
                {members
                  .filter((member) => member.status === 'active')
                  .map((member) => (
                    <option key={member.userId} value={member.userId}>
                      {member.displayName ?? member.email}
                    </option>
                  ))}
              </select>
            </label>
          ) : null}
          {newCategory === 'other' && newType === 'other_resource' ? (
            <label>
              Other subtype
              <input
                required
                value={newOtherSubtype}
                onChange={(event) => setNewOtherSubtype(event.target.value)}
              />
            </label>
          ) : null}
          <label className="resources-form-wide">
            Description
            <textarea
              value={newDescription}
              onChange={(event) => setNewDescription(event.target.value)}
            />
          </label>
          <div className="resources-form-actions">
            <button disabled={busy} type="submit">
              Create Resource
            </button>
            <span>Externally sourced Learning and Assessment resources begin as Unverified.</span>
          </div>
        </form>
      ) : null}

      <div className="resources-register-toolbar">
        <label>
          <span className="visually-hidden">Search Resources</span>
          <input
            placeholder="Search name, ID, asset, supplier or training product"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <select
          aria-label="Filter Resources by category"
          value={category}
          onChange={(event) => setCategory(event.target.value as ResourceCategory | '')}
        >
          <option value="">All categories</option>
          {categories.map((value) => (
            <option key={value} value={value}>
              {categoryLabels[value]}
            </option>
          ))}
        </select>
        <label className="resources-check">
          <input
            checked={alertsOnly}
            type="checkbox"
            onChange={(event) => setAlertsOnly(event.target.checked)}
          />{' '}
          Alerts only
        </label>
        {category || alertsOnly || search ? (
          <button
            className="button-secondary"
            type="button"
            onClick={() => {
              setSearch('');
              setCategory('');
              setAlertsOnly(false);
            }}
          >
            Clear filters
          </button>
        ) : null}
      </div>

      <div className="resources-view-toolbar" aria-label="Saved Resource views and reports">
        <label>
          Saved view
          <select value={activeViewId} onChange={(event) => applySavedView(event.target.value)}>
            <option value="">Choose saved view</option>
            {savedViews.map((view) => (
              <option key={view.id} value={view.id}>
                {view.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Report template
          <select value={reportType} onChange={(event) => setReportType(event.target.value)}>
            {[
              ['complete_register', 'Complete Resource Register'],
              ['category_register', 'Resource Register by Category'],
              ['location_register', 'Resource Register by Location'],
              ['training_product_register', 'Training Product Resource Register'],
              ['review_schedule', 'Resource Review Schedule'],
              ['maintenance_schedule', 'Maintenance Schedule'],
              ['expiry_register', 'Expiry Register'],
              ['unverified_resources', 'Unverified Resources'],
              ['awaiting_approval', 'Awaiting Approval'],
              ['document_register', 'Document Register'],
              ['training_learning_register', 'Training & Learning Register'],
              ['assessment_register', 'Assessment Resource Register'],
              ['digital_licence_register', 'Digital Licence Register'],
              ['vehicle_register', 'Vehicle Register'],
              ['equipment_asset_register', 'Equipment / Asset Register'],
              ['personnel_allocation', 'Personnel Resource Allocation'],
            ].map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <button
          className="button-secondary"
          disabled={busy}
          type="button"
          onClick={() => void saveCurrentView()}
        >
          Save view
        </button>
        {activeViewId ? (
          <button
            className="button-secondary"
            disabled={busy}
            type="button"
            onClick={() => void deleteSavedView()}
          >
            Delete view
          </button>
        ) : null}
      </div>

      {selectedIds.length ? (
        <div className="resources-bulk-toolbar" aria-label="Bulk Resource actions">
          <strong>{selectedIds.length} selected</strong>
          <select
            aria-label="Bulk action"
            value={bulkAction}
            onChange={(event) => setBulkAction(event.target.value as typeof bulkAction)}
          >
            <option value="archive">Archive</option>
            <option value="assign_locations">Assign Location</option>
            <option value="assign_owner">Assign owner</option>
            <option value="update_review_date">Set review date</option>
            <option value="map_training_product">Map training product</option>
          </select>
          {bulkAction === 'assign_locations' ? (
            <select
              aria-label="Bulk Location"
              value={bulkLocationId}
              onChange={(event) => setBulkLocationId(event.target.value)}
            >
              <option value="">Select Location</option>
              {locations
                .filter((location) => location.status !== 'archived')
                .map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
            </select>
          ) : null}
          {bulkAction === 'assign_owner' ? (
            <select
              aria-label="Bulk owner"
              value={bulkOwnerId}
              onChange={(event) => setBulkOwnerId(event.target.value)}
            >
              <option value="">Select owner</option>
              {members
                .filter((member) => member.status === 'active')
                .map((member) => (
                  <option key={member.userId} value={member.userId}>
                    {member.displayName ?? member.email}
                  </option>
                ))}
            </select>
          ) : null}
          {bulkAction === 'update_review_date' ? (
            <input
              aria-label="Bulk review date"
              type="date"
              value={bulkReviewDate}
              onChange={(event) => setBulkReviewDate(event.target.value)}
            />
          ) : null}
          {bulkAction === 'map_training_product' ? (
            <>
              <select
                aria-label="Bulk training product"
                value={mappingCode}
                onChange={(event) => setMappingCode(event.target.value)}
              >
                <option value="">Select training product</option>
                {scope.map((item) => (
                  <option key={item.nationalCode} value={item.nationalCode}>
                    {item.nationalCode} — {item.title}
                  </option>
                ))}
              </select>
              <input
                aria-label="Bulk release number"
                placeholder="Release"
                value={mappingRelease}
                onChange={(event) => setMappingRelease(event.target.value)}
              />
            </>
          ) : null}
          <button disabled={busy} type="button" onClick={() => void applyBulkAction()}>
            Apply
          </button>
          <button
            className="button-secondary"
            disabled={busy}
            type="button"
            onClick={() => setSelectedIds([])}
          >
            Clear
          </button>
        </div>
      ) : null}

      <div className="resources-register-layout">
        <div
          className="resources-register-table"
          role="region"
          aria-label="Resource Register"
          tabIndex={0}
        >
          {loading ? <p>Loading Resources…</p> : null}
          {!loading && items.length === 0 ? (
            <p>No resources match these filters. Add your first resource to create the register.</p>
          ) : null}
          {!loading && items.length ? (
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      aria-label="Select all visible Resources"
                      checked={items.every((item) => selectedIds.includes(item.id))}
                      type="checkbox"
                      onChange={(event) =>
                        setSelectedIds(
                          event.target.checked
                            ? [...new Set([...selectedIds, ...items.map((item) => item.id)])]
                            : selectedIds.filter((id) => !items.some((item) => item.id === id)),
                        )
                      }
                    />
                  </th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Location</th>
                  <th>Assurance</th>
                  <th>Availability</th>
                  <th>Training products</th>
                  <th>Alert</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr className={selected?.id === item.id ? 'is-selected' : ''} key={item.id}>
                    <td>
                      <input
                        aria-label={`Select ${item.name}`}
                        checked={selectedIds.includes(item.id)}
                        type="checkbox"
                        onChange={(event) =>
                          setSelectedIds((current) =>
                            event.target.checked
                              ? [...new Set([...current, item.id])]
                              : current.filter((id) => id !== item.id),
                          )
                        }
                      />
                    </td>
                    <td>
                      <button type="button" onClick={() => void select(item.id)}>
                        <strong>{item.name}</strong>
                        <small>
                          {item.resourceNumber} · {typeLabel(item.type)}
                        </small>
                      </button>
                    </td>
                    <td>{categoryLabels[item.category]}</td>
                    <td>
                      {item.locations
                        .map(
                          (location) =>
                            selectedLocationLabels.get(location.locationId) ??
                            statusLabel(location.role),
                        )
                        .join(', ') || 'Organisation-wide'}
                    </td>
                    <td>
                      <span className={`resources-badge${badgeClass(item.assuranceStatus)}`}>
                        {statusLabel(item.assuranceStatus)}
                      </span>
                    </td>
                    <td>
                      <span className={`resources-badge${badgeClass(item.availability)}`}>
                        {statusLabel(item.availability)}
                      </span>
                    </td>
                    <td>
                      {item.trainingProducts
                        .map((mapping) => `${mapping.productCode} ${mapping.releaseNumber}`)
                        .join(', ') || '—'}
                    </td>
                    <td>
                      {item.alerts.length ? (
                        <span className="resources-alert-count">{item.alerts.length}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>

        {selected ? (
          <article className="resources-item" aria-live="polite">
            <header>
              <div>
                <p className="eyebrow">{categoryLabels[selected.category]}</p>
                <h2>{selected.name}</h2>
                <p>
                  {selected.resourceNumber} · {typeLabel(selected.type)}
                </p>
              </div>
              <div className="resources-status-stack">
                <span className={`resources-badge${badgeClass(selected.health)}`}>
                  {statusLabel(selected.health)}
                </span>
                <span className={`resources-badge${badgeClass(selected.assuranceStatus)}`}>
                  {statusLabel(selected.assuranceStatus)}
                </span>
              </div>
            </header>
            <div aria-label="Resource item sections" className="resources-item-tabs" role="tablist">
              <button
                aria-selected={selectedTab === 'summary'}
                role="tab"
                type="button"
                onClick={() => setSelectedTab('summary')}
              >
                Summary
              </button>
              <button
                aria-selected={selectedTab === 'details'}
                role="tab"
                type="button"
                onClick={() => setSelectedTab('details')}
              >
                Details
              </button>
            </div>
            {selectedTab === 'summary' ? (
              <div className="resources-summary" role="tabpanel">
                <dl>
                  <div>
                    <dt>Lifecycle</dt>
                    <dd>
                      <span className={`resources-badge${badgeClass(selected.lifecycle)}`}>
                        {statusLabel(selected.lifecycle)}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt>Availability</dt>
                    <dd>
                      <span className={`resources-badge${badgeClass(selected.availability)}`}>
                        {statusLabel(selected.availability)}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt>Current version</dt>
                    <dd>
                      {selected.currentApprovedVersion?.versionNumber ?? 'No approved version'}
                    </dd>
                  </div>
                  <div>
                    <dt>Locations</dt>
                    <dd>
                      {selected.locations
                        .map(
                          (location) =>
                            selectedLocationLabels.get(location.locationId) ??
                            statusLabel(location.role),
                        )
                        .join(', ') || 'Organisation-wide'}
                    </dd>
                  </div>
                  <div>
                    <dt>Owner</dt>
                    <dd>{selectedMemberLabels.get(selected.ownerId ?? '') ?? 'Not assigned'}</dd>
                  </div>
                  <div>
                    <dt>Custodian</dt>
                    <dd>
                      {selectedMemberLabels.get(selected.responsiblePersonId ?? '') ??
                        'Not assigned'}
                    </dd>
                  </div>
                  <div>
                    <dt>Training products</dt>
                    <dd>
                      {selected.trainingProducts
                        .map((mapping) => `${mapping.productCode} Release ${mapping.releaseNumber}`)
                        .join(', ') || 'Not mapped'}
                    </dd>
                  </div>
                  <div>
                    <dt>Provision</dt>
                    <dd>{statusLabel(selected.provision)}</dd>
                  </div>
                </dl>
                <section className="resources-alert-card">
                  <h3>Active alerts</h3>
                  {selected.alerts.length ? (
                    <ul>
                      {selected.alerts.map((alert) => (
                        <li key={alert}>{alert}</li>
                      ))}
                    </ul>
                  ) : (
                    <p>No current alerts.</p>
                  )}
                </section>
                <div className="resources-summary-actions">
                  <button
                    disabled={busy}
                    type="button"
                    onClick={() => void updateSelected('submit-for-review', {})}
                  >
                    Submit for review
                  </button>
                  {canApprove ? (
                    <button
                      className="button-secondary"
                      disabled={busy}
                      type="button"
                      onClick={() => void updateSelected('approve', { decision: 'approved' })}
                    >
                      Approve for use
                    </button>
                  ) : null}
                  {selected.trainingProducts.length ? (
                    <button
                      className="button-secondary"
                      disabled={busy}
                      type="button"
                      onClick={() => void printEvidencePack()}
                    >
                      Print evidence pack
                    </button>
                  ) : null}
                  <button
                    className="button-secondary"
                    disabled={busy}
                    type="button"
                    onClick={() =>
                      void updateSelected(
                        selected.lifecycle === 'archived' ? 'restore' : 'archive',
                        {},
                      )
                    }
                  >
                    {selected.lifecycle === 'archived' ? 'Restore' : 'Archive'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="resources-details" role="tabpanel">
                <details open>
                  <summary>Identification & category details</summary>
                  {editing ? (
                    <form
                      className="resources-create-form resources-edit-form"
                      onSubmit={(event) => void saveEdit(event)}
                    >
                      <label>
                        Resource name
                        <input
                          required
                          value={editName}
                          onChange={(event) => setEditName(event.target.value)}
                        />
                      </label>
                      <label>
                        Internal reference
                        <input
                          value={editInternalReference}
                          onChange={(event) => setEditInternalReference(event.target.value)}
                        />
                      </label>
                      <label>
                        Asset number
                        <input
                          value={editAssetNumber}
                          onChange={(event) => setEditAssetNumber(event.target.value)}
                        />
                      </label>
                      <label>
                        Supplier / provider
                        <input
                          value={editSupplier}
                          onChange={(event) => setEditSupplier(event.target.value)}
                        />
                      </label>
                      <label>
                        Availability
                        <select
                          value={editAvailability}
                          onChange={(event) => setEditAvailability(event.target.value)}
                        >
                          {[
                            'available',
                            'temporarily_unavailable',
                            'restricted',
                            'under_maintenance',
                            'out_of_service',
                            'withdrawn',
                          ].map((value) => (
                            <option key={value} value={value}>
                              {statusLabel(value)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Lifecycle
                        <select
                          value={editLifecycle}
                          onChange={(event) => setEditLifecycle(event.target.value)}
                        >
                          {['draft', 'active', 'suspended', 'retired', 'archived'].map((value) => (
                            <option key={value} value={value}>
                              {statusLabel(value)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="resources-form-wide">
                        Description
                        <textarea
                          value={editDescription}
                          onChange={(event) => setEditDescription(event.target.value)}
                        />
                      </label>
                      <CategoryDetailsEditor
                        category={selected.category}
                        details={editDetails}
                        onChange={(key, value) =>
                          setEditDetails((current) => ({ ...current, [key]: value }))
                        }
                      />
                      <div className="resources-form-actions">
                        <button disabled={busy} type="submit">
                          Save Resource details
                        </button>
                        <button
                          className="button-secondary"
                          disabled={busy}
                          type="button"
                          onClick={() => setEditing(false)}
                        >
                          Discard changes
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <dl>
                        <div>
                          <dt>Description</dt>
                          <dd>{selected.description ?? 'Not recorded'}</dd>
                        </div>
                        <div>
                          <dt>Internal reference</dt>
                          <dd>{selected.internalReference ?? 'Not recorded'}</dd>
                        </div>
                        <div>
                          <dt>Asset number</dt>
                          <dd>{selected.assetNumber ?? 'Not recorded'}</dd>
                        </div>
                        <div>
                          <dt>Tags</dt>
                          <dd>{selected.tags.join(', ') || 'None'}</dd>
                        </div>
                      </dl>
                      <button
                        className="button-secondary"
                        disabled={busy}
                        type="button"
                        onClick={beginEdit}
                      >
                        Edit Resource details
                      </button>
                    </>
                  )}
                </details>
                <details>
                  <summary>Provision & ownership</summary>
                  <dl>
                    <div>
                      <dt>Provision</dt>
                      <dd>{statusLabel(selected.provision)}</dd>
                    </div>
                    <div>
                      <dt>Supplier / provider</dt>
                      <dd>{selected.supplier ?? 'Not recorded'}</dd>
                    </div>
                    <div>
                      <dt>Acquisition</dt>
                      <dd>{dateLabel(selected.acquisitionDate)}</dd>
                    </div>
                    <div>
                      <dt>Agreement</dt>
                      <dd>{selected.agreementReference ?? 'Not recorded'}</dd>
                    </div>
                  </dl>
                </details>
                <details>
                  <summary>Locations & Training products</summary>
                  <p>
                    {selected.locations
                      .map(
                        (location) =>
                          selectedLocationLabels.get(location.locationId) ??
                          statusLabel(location.role),
                      )
                      .join(', ') || 'Organisation-wide'}
                  </p>
                  <ul>
                    {selected.trainingProducts.map((mapping) => (
                      <li key={mapping.id}>
                        <strong>
                          {mapping.productCode} Release {mapping.releaseNumber}
                        </strong>{' '}
                        — {mapping.title} · {statusLabel(mapping.assuranceStatus)}
                        {mapping.reviewRequired ? ' · Review required' : ''}
                        {mapping.reviewedAt ? ` · reviewed ${dateLabel(mapping.reviewedAt)}` : ''}
                        {canApprove &&
                        mapping.assuranceStatus !== 'approved' &&
                        mapping.assuranceStatus !== 'approved_with_conditions' ? (
                          <button
                            className="button-secondary"
                            disabled={busy}
                            type="button"
                            onClick={() =>
                              void updateSelected(`training-products/${mapping.id}/approve`, {
                                decision: 'approved',
                              })
                            }
                          >
                            Approve mapping
                          </button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  {scope.length ? (
                    <form
                      className="resources-inline-form"
                      onSubmit={(event) => {
                        event.preventDefault();
                        const selectedScope = scope.find(
                          (item) => item.nationalCode === mappingCode,
                        );
                        if (!selectedScope || !mappingRelease.trim()) return;
                        void updateSelected('training-products', {
                          productCode: selectedScope.nationalCode,
                          title: selectedScope.title,
                          productType: selectedScope.trainingComponentType,
                          releaseNumber: mappingRelease.trim(),
                          releaseIdentifier: `${selectedScope.nationalCode}#${mappingRelease.trim()}`,
                          relationship: 'supports delivery',
                          locationIds: selected.locations.map((location) => location.locationId),
                          deliveryModes: [],
                          assuranceStatus: 'under_review',
                          unitMappings: [],
                        });
                      }}
                    >
                      <select
                        aria-label="Training product"
                        value={mappingCode}
                        onChange={(event) => setMappingCode(event.target.value)}
                      >
                        <option value="">Map training product</option>
                        {scope.map((item) => (
                          <option key={item.nationalCode} value={item.nationalCode}>
                            {item.nationalCode} — {item.title}
                          </option>
                        ))}
                      </select>
                      <input
                        aria-label="Release number"
                        placeholder="Release number"
                        value={mappingRelease}
                        onChange={(event) => setMappingRelease(event.target.value)}
                      />
                      <button disabled={busy || !mappingCode || !mappingRelease} type="submit">
                        Map
                      </button>
                    </form>
                  ) : (
                    <p className="resources-note">
                      Connect and verify the organisation’s TGA record to map training product
                      releases.
                    </p>
                  )}
                </details>
                <details>
                  <summary>Files & versions</summary>
                  <p>
                    {selected.files.length} linked file(s) · {selected.versions.length} version(s)
                  </p>
                  <ul>
                    {selected.files.map((file) => (
                      <li key={file.id}>
                        {file.fileName} · {(file.size / 1024).toFixed(1)} KB
                      </li>
                    ))}
                  </ul>
                  <ul>
                    {selected.versions.map((version) => (
                      <li key={version.id}>
                        {version.versionNumber} — {version.title}{' '}
                        <span className={`resources-badge${badgeClass(version.status)}`}>
                          {statusLabel(version.status)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <form
                    className="resources-inline-form"
                    onSubmit={(event) => void uploadResourceFile(event)}
                  >
                    <input
                      aria-label="Resource file"
                      type="file"
                      onChange={(event) => setResourceFile(event.target.files?.[0] ?? null)}
                    />
                    <input
                      aria-label="File description"
                      placeholder="File description (optional)"
                      value={resourceFileDescription}
                      onChange={(event) => setResourceFileDescription(event.target.value)}
                    />
                    <button disabled={busy || !resourceFile} type="submit">
                      Upload file
                    </button>
                  </form>
                  <p className="resources-note">
                    Files are tenant-isolated and signed. Attach files to a draft version before
                    approval; approved version files remain immutable.
                  </p>
                </details>
                <details>
                  <summary>Approvals & reviews</summary>
                  <p>
                    {selected.approvals.length} approval record(s) · {selected.reviews.length}{' '}
                    review record(s)
                  </p>
                  <ul>
                    {selected.approvals.map((approval) => (
                      <li key={approval.id}>
                        {approval.decision ? statusLabel(approval.decision) : 'Under review'} ·
                        submitted {dateLabel(approval.submittedAt)} ·{' '}
                        {approval.approverId
                          ? (selectedMemberLabels.get(approval.approverId) ?? approval.approverId)
                          : 'Approver pending'}
                      </li>
                    ))}
                  </ul>
                  <ul>
                    {selected.reviews.map((review) => (
                      <li key={review.id}>
                        {statusLabel(review.reason)} —{' '}
                        <span className={`resources-badge${badgeClass(review.status)}`}>
                          {statusLabel(review.status)}
                        </span>
                        {review.completedAt ? ` · completed ${dateLabel(review.completedAt)}` : ''}
                      </li>
                    ))}
                  </ul>
                  <form
                    className="resources-inline-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void updateSelected('reviews', {
                        reason: reviewReason,
                        status: 'scheduled',
                        ...(reviewDueAt
                          ? { nextReviewAt: new Date(reviewDueAt).toISOString() }
                          : {}),
                        evidenceFileIds: [],
                        responses: [],
                      });
                    }}
                  >
                    <select
                      aria-label="Review reason"
                      value={reviewReason}
                      onChange={(event) => setReviewReason(event.target.value)}
                    >
                      {[
                        'scheduled_periodic',
                        'new_resource_verification',
                        'new_training_product',
                        'new_release',
                        'location_change',
                        'delivery_mode_change',
                        'incident_triggered',
                        'complaint_triggered',
                        'industry_change',
                        'major_version',
                        'other_party_arrangement',
                        'ad_hoc',
                        'personnel_change',
                        'approval_expiry',
                        'maintenance_overdue',
                        'resource_unavailable',
                      ].map((value) => (
                        <option key={value} value={value}>
                          {statusLabel(value)}
                        </option>
                      ))}
                    </select>
                    <input
                      aria-label="Next review date"
                      type="date"
                      value={reviewDueAt}
                      onChange={(event) => setReviewDueAt(event.target.value)}
                    />
                    <button disabled={busy} type="submit">
                      Schedule review
                    </button>
                  </form>
                  {selected.reviews.some((review) =>
                    ['scheduled', 'in_progress'].includes(review.status),
                  ) ? (
                    <form
                      className="resources-inline-form resources-completion-form"
                      onSubmit={(event) => void completeReview(event)}
                    >
                      <select
                        aria-label="Review to complete"
                        required
                        value={completeReviewId}
                        onChange={(event) => setCompleteReviewId(event.target.value)}
                      >
                        <option value="">Complete a scheduled review</option>
                        {selected.reviews
                          .filter((review) => ['scheduled', 'in_progress'].includes(review.status))
                          .map((review) => (
                            <option key={review.id} value={review.id}>
                              {statusLabel(review.reason)} · {dateLabel(review.nextReviewAt)}
                            </option>
                          ))}
                      </select>
                      <select
                        aria-label="Review decision"
                        value={reviewDecision}
                        onChange={(event) => setReviewDecision(event.target.value)}
                      >
                        {[
                          'suitable',
                          'suitable_with_actions',
                          'not_suitable',
                          'review_required',
                        ].map((value) => (
                          <option key={value} value={value}>
                            {statusLabel(value)}
                          </option>
                        ))}
                      </select>
                      <input
                        aria-label="Review findings"
                        placeholder="Findings"
                        value={reviewFindings}
                        onChange={(event) => setReviewFindings(event.target.value)}
                      />
                      <input
                        aria-label="Required actions"
                        placeholder="Required actions"
                        value={reviewActions}
                        onChange={(event) => setReviewActions(event.target.value)}
                      />
                      <select
                        aria-label="Review evidence files"
                        multiple
                        value={reviewEvidenceFileIds}
                        onChange={(event) =>
                          setReviewEvidenceFileIds(
                            Array.from(
                              event.currentTarget.selectedOptions,
                              (option) => option.value,
                            ),
                          )
                        }
                      >
                        {selected.files.map((file) => (
                          <option key={file.id} value={file.fileId}>
                            {file.fileName}
                          </option>
                        ))}
                      </select>
                      <input
                        aria-label="Next review date after completion"
                        type="date"
                        value={reviewNextDueAt}
                        onChange={(event) => setReviewNextDueAt(event.target.value)}
                      />
                      <button disabled={busy || !completeReviewId} type="submit">
                        Complete review
                      </button>
                    </form>
                  ) : null}
                </details>
                <details>
                  <summary>Maintenance / inspections</summary>
                  <ul>
                    {selected.maintenanceSchedules.map((schedule) => (
                      <li key={schedule.id}>
                        {statusLabel(schedule.maintenanceType)} — next due{' '}
                        {dateLabel(schedule.nextDueAt)} · {statusLabel(schedule.status)}
                      </li>
                    ))}
                  </ul>
                  <ul>
                    {selected.maintenanceEvents.map((event) => (
                      <li key={event.id}>
                        {statusLabel(event.maintenanceType)} — {statusLabel(event.outcome)} ·
                        completed {dateLabel(event.completedAt)}
                      </li>
                    ))}
                  </ul>
                  <form
                    className="resources-inline-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (!maintenanceDueAt) return;
                      void updateSelected('maintenance-schedules', {
                        maintenanceType,
                        nextDueAt: new Date(maintenanceDueAt).toISOString(),
                        evidenceRequired: false,
                        status: 'active',
                      });
                    }}
                  >
                    <select
                      aria-label="Maintenance type"
                      value={maintenanceType}
                      onChange={(event) => setMaintenanceType(event.target.value)}
                    >
                      {[
                        'inspection',
                        'servicing',
                        'calibration',
                        'registration',
                        'insurance',
                        'certification',
                        'warranty_review',
                        'safety_check',
                        'scheduled_maintenance',
                        'ad_hoc_repair',
                      ].map((value) => (
                        <option key={value} value={value}>
                          {statusLabel(value)}
                        </option>
                      ))}
                    </select>
                    <input
                      aria-label="Maintenance due date"
                      type="date"
                      value={maintenanceDueAt}
                      onChange={(event) => setMaintenanceDueAt(event.target.value)}
                    />
                    <button disabled={busy || !maintenanceDueAt} type="submit">
                      Schedule
                    </button>
                  </form>
                  {selected.maintenanceSchedules.filter((schedule) => schedule.status === 'active')
                    .length ? (
                    <form
                      className="resources-inline-form resources-completion-form"
                      onSubmit={(event) => void completeMaintenance(event)}
                    >
                      <select
                        aria-label="Maintenance schedule to complete"
                        required
                        value={maintenanceScheduleId}
                        onChange={(event) => setMaintenanceScheduleId(event.target.value)}
                      >
                        <option value="">Complete a maintenance task</option>
                        {selected.maintenanceSchedules
                          .filter((schedule) => schedule.status === 'active')
                          .map((schedule) => (
                            <option key={schedule.id} value={schedule.id}>
                              {statusLabel(schedule.maintenanceType)} · due{' '}
                              {dateLabel(schedule.nextDueAt)}
                            </option>
                          ))}
                      </select>
                      <select
                        aria-label="Maintenance outcome"
                        value={maintenanceOutcome}
                        onChange={(event) => setMaintenanceOutcome(event.target.value)}
                      >
                        {['passed', 'passed_with_actions', 'failed', 'not_completed'].map(
                          (value) => (
                            <option key={value} value={value}>
                              {statusLabel(value)}
                            </option>
                          ),
                        )}
                      </select>
                      <input
                        aria-label="Maintenance issues"
                        placeholder="Issues identified"
                        value={maintenanceIssues}
                        onChange={(event) => setMaintenanceIssues(event.target.value)}
                      />
                      <input
                        aria-label="Maintenance cost"
                        placeholder="Cost"
                        min="0"
                        step="0.01"
                        type="number"
                        value={maintenanceCost}
                        onChange={(event) => setMaintenanceCost(event.target.value)}
                      />
                      <select
                        aria-label="Maintenance evidence files"
                        multiple
                        value={maintenanceEvidenceFileIds}
                        onChange={(event) =>
                          setMaintenanceEvidenceFileIds(
                            Array.from(
                              event.currentTarget.selectedOptions,
                              (option) => option.value,
                            ),
                          )
                        }
                      >
                        {selected.files.map((file) => (
                          <option key={file.id} value={file.fileId}>
                            {file.fileName}
                          </option>
                        ))}
                      </select>
                      <input
                        aria-label="Next maintenance date"
                        type="date"
                        value={maintenanceNextDueAt}
                        onChange={(event) => setMaintenanceNextDueAt(event.target.value)}
                      />
                      <button disabled={busy || !maintenanceScheduleId} type="submit">
                        Record completion
                      </button>
                    </form>
                  ) : null}
                </details>
                <details>
                  <summary>Key dates</summary>
                  <ul>
                    {selected.keyDates.map((date) => (
                      <li key={date.id}>
                        {statusLabel(date.type)} — {dateLabel(date.dueAt)}{' '}
                        {date.completedAt ? '· complete' : ''}
                      </li>
                    ))}
                  </ul>
                  <form
                    className="resources-inline-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (!keyDateDueAt) return;
                      void updateSelected('key-dates', {
                        type: keyDateType,
                        dueAt: new Date(keyDateDueAt).toISOString(),
                        reminderDays: [30, 14, 7, 0],
                      });
                    }}
                  >
                    <select
                      aria-label="Key date type"
                      value={keyDateType}
                      onChange={(event) => setKeyDateType(event.target.value)}
                    >
                      {[
                        'review_due',
                        'approval_expiry',
                        'maintenance_due',
                        'inspection_due',
                        'registration_expiry',
                        'insurance_expiry',
                        'licence_renewal',
                        'subscription_renewal',
                        'warranty_expiry',
                        'agreement_expiry',
                        'credential_expiry',
                        'document_review',
                        'version_review',
                      ].map((value) => (
                        <option key={value} value={value}>
                          {statusLabel(value)}
                        </option>
                      ))}
                    </select>
                    <input
                      aria-label="Key date"
                      type="date"
                      value={keyDateDueAt}
                      onChange={(event) => setKeyDateDueAt(event.target.value)}
                    />
                    <button disabled={busy || !keyDateDueAt} type="submit">
                      Add date
                    </button>
                  </form>
                </details>
                <details>
                  <summary>Related resources</summary>
                  {selected.relationships.length ? (
                    <ul>
                      {selected.relationships.map((relationship) => (
                        <li key={relationship.id}>
                          {items.find((item) => item.id === relationship.relatedResourceId)?.name ??
                            relationship.relatedResourceId}{' '}
                          — {statusLabel(relationship.relationship)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No related resources recorded.</p>
                  )}
                  <form
                    className="resources-inline-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (!relatedResourceId) return;
                      void updateSelected('relationships', {
                        relatedResourceId,
                        relationship: relationshipType,
                      });
                    }}
                  >
                    <select
                      aria-label="Related resource"
                      value={relatedResourceId}
                      onChange={(event) => setRelatedResourceId(event.target.value)}
                    >
                      <option value="">Select related resource</option>
                      {items
                        .filter((item) => item.id !== selected.id)
                        .map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                    </select>
                    <select
                      aria-label="Relationship type"
                      value={relationshipType}
                      onChange={(event) => setRelationshipType(event.target.value)}
                    >
                      {[
                        'located_in',
                        'contains',
                        'requires',
                        'used_with',
                        'supported_by',
                        'replaces',
                        'supersedes',
                        'alternative_to',
                      ].map((value) => (
                        <option key={value} value={value}>
                          {statusLabel(value)}
                        </option>
                      ))}
                    </select>
                    <button disabled={busy || !relatedResourceId} type="submit">
                      Link
                    </button>
                  </form>
                </details>
                <details>
                  <summary>Issues & actions</summary>
                  <ul>
                    {selected.issues.map((issue) => (
                      <li key={issue.id}>
                        <strong>{issue.title}</strong> — {statusLabel(issue.severity)} ·{' '}
                        {statusLabel(issue.status)}
                      </li>
                    ))}
                  </ul>
                  <form
                    className="resources-inline-form resources-issue-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (!issueTitle.trim() || !issueDescription.trim()) return;
                      void updateSelected('issues', {
                        title: issueTitle.trim(),
                        description: issueDescription.trim(),
                        severity: 'medium',
                        source: 'Resource register',
                        status: 'open',
                      });
                      setIssueTitle('');
                      setIssueDescription('');
                    }}
                  >
                    <input
                      aria-label="Issue title"
                      placeholder="Issue title"
                      value={issueTitle}
                      onChange={(event) => setIssueTitle(event.target.value)}
                    />
                    <input
                      aria-label="Issue description"
                      placeholder="Describe the issue"
                      value={issueDescription}
                      onChange={(event) => setIssueDescription(event.target.value)}
                    />
                    <button
                      disabled={busy || !issueTitle.trim() || !issueDescription.trim()}
                      type="submit"
                    >
                      Record issue
                    </button>
                  </form>
                </details>
                <details>
                  <summary>Notes & audit history</summary>
                  <p>{selected.notes ?? 'No notes recorded.'}</p>
                  <p className="resources-note">
                    Every creation, update, mapping, file, version, approval, review, maintenance,
                    issue, archive and restore action is retained in the Organisation Activity audit
                    history.
                  </p>
                </details>
              </div>
            )}
          </article>
        ) : (
          <aside className="resources-empty-detail">
            <strong>Select a resource</strong>
            <span>Choose a register item to see its concise Summary and full Details record.</span>
          </aside>
        )}
      </div>
    </section>
  );
}
